import { useMutation, useQuery } from "@tanstack/react-query"
import { Card, Divider, message } from "antd"
import type React from "react"
import { useEffect, useState } from "react"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"
import { type VideoFilters, videoService } from "@/services/video/videoApi"
import { VideoCreateModal } from "./components/VideoCreateModal"
import { VideoSearchForm } from "./components/VideoSearchForm"
import { VideoTable } from "./components/VideoTable"
import { VideoTableActions } from "./components/VideoTableActions"

export const VideoPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<VideoFilters>({
    ...PAGE_CONFIG,
    search: "",
    status: undefined,
    is_public: undefined,
  })

  const [total, setTotal] = useState<number>(0)
  const [createOpen, setCreateOpen] = useState(false)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["videos_list", searchParams],
    queryFn: () => videoService.list(searchParams),
  })

  useEffect(() => {
    if (result?.total !== undefined) {
      setTotal(result.total)
    }
  }, [result?.total])

  const deleteMutation = useMutation({
    mutationFn: (id: string) => videoService.delete(id),
    onSuccess: () => {
      message.success("Xóa video thành công")
      refetch()
    },
    onError: (err: unknown) => {
      message.error(err instanceof Error ? err.message : "Xóa thất bại")
    },
  })

  const handleSearch = (values: Omit<VideoFilters, "page" | "per_page" | "limit">) => {
    setSearchParams({
      ...values,
      page: 1,
      per_page: searchParams.per_page,
    })
  }

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id)
  }

  const tableLoading = isFetching || deleteMutation.isPending

  return (
    <div className="flex h-full flex-col gap-2">
      <VideoSearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          status: searchParams.status,
          is_public: searchParams.is_public,
        }}
      />

      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow flex-col overflow-hidden p-3" }}
        title={
          <div className="flex items-center">
            <h2 className="font-semibold text-base text-gray-800">Quản lý video học tập</h2>
            <Divider orientation="vertical" />
            <VideoTableActions onAdd={() => setCreateOpen(true)} onRefresh={refetch} loading={tableLoading} />
          </div>
        }
      >
        <VideoTable
          data={result?.videos || []}
          loading={tableLoading}
          onDelete={handleDelete}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      <VideoCreateModal open={createOpen} onClose={() => setCreateOpen(false)} onSuccess={refetch} />
    </div>
  )
}

export default VideoPage
